<script setup lang="ts">
// ================================
// IMPORTS
// ================================
import {
    CAPACITY_METHOD_PUISSANCE,
    DEFAULT_COEFF_CAPACITE_VALUE,
    DEFAULT_PRICER_CAPACITY_VALUE,
    OptionTarifaire,
} from "@modules/courtage/constants"
import { PeriodItem } from "@types"
import moment from "moment"
import { onMounted, ref, useTemplateRef } from "vue"
import { useI18n } from "vue-i18n"
import { useForm } from "vee-validate"
import * as yup from "yup"

// ================================
// INTERNATIONALIZATION
// ================================
const { t } = useI18n({
    messages: {
        fr: {
            title: "Périodes de fourniture et tarifs",
            supplier: "Nom du fournisseur",
            offer_name: "Nom de l'offre",
            offer_type: "Type de l'offre",
            add_period: "Ajouter une période",
            edit: ({ named }: { named: (key: string) => string }) =>
                named("edit") ? "Valider" : "Remplir",
        },
    },
})

// ================================
// TYPES & INTERFACES
// ================================
/**
 * Propriétés du composant de gestion des périodes
 */
type Props = {
    /** Indique si l'offre est actuelle (non modifiable) */
    actual: boolean
    /** Type d'offre tarifaire sélectionné */
    offer: OptionTarifaire
    /** Liste des périodes de l'offre */
    periods: PeriodItem[]
    /** Modèle de consommation par poste pour l'offre */
    energieparposteElec?: {
        base: number
        hp: number
        hc: number
        hph: number
        hch: number
        hpb: number
        hcb: number
        pte: number
    }
}

/**
 * Payload de mise à jour d'une période
 */
interface PeriodUpdateEvent {
    /** Indique si la période est valide */
    valid: boolean
    /** Données de la période */
    period: PeriodItem
}

// ================================
// PROPS & EMITS
// ================================
withDefaults(defineProps<Props>(), {
    actual: false,
    offer: OptionTarifaire.BASE,
    periods: () => [],
    energieparposteElec: undefined,
})

defineEmits<{
    /** Événement émis lorsque la liste des périodes est mise à jour */
    (e: "update:periods", value: PeriodItem[]): void
}>()

// ================================
// REACTIVE STATE
// ================================
/** Liste des périodes avec liaison bidirectionnelle */
const periodList = defineModel<PeriodItem[]>("periods", {
    default: ref([]),
})

const periodItemRef = useTemplateRef<any[]>("periodItemRef")

/** État de validation globale des périodes */
const hasError = ref<boolean>(false)

const active = ref<string | null>(null)

// ================================
// LIFECYCLE
// ================================
/**
 * Initialisation du composant
 * Ajoute automatiquement une première période si la liste est vide
 */
onMounted(() => {
    if (!periodList.value?.length) {
        onClickAddPeriod()
    }
})

// ================================
// FORM CONFIGURATION
// ================================
/**
 * Configuration de validation du formulaire des périodes
 */
const { validate } = useForm({
    validationSchema: yup.object({
        periodList: yup.array().of(
            yup.object({
                nom_periode: yup.string().required(),
                idperiode: yup.string().required(),
            })
        ),
    }),
})

// ================================
// METHODS
// ================================
/**
 * Crée une nouvelle période avec des valeurs par défaut
 * @returns Nouvelle période initialisée
 */
function createDefaultPeriod(): PeriodItem {
    let defaultYear = moment().format("YYYY")
    if (periodList.value && periodList.value.length > 0) {
        const lastPeriod = periodList.value[periodList.value.length - 1]
        const lastYear = parseInt(lastPeriod.idperiode)
        defaultYear = (lastYear + 1).toString()
    }

    return {
        idperiode: defaultYear,
        name: `Période ${(periodList.value?.length || 0) + 1}`,
        cee: 0,
        capacite_obligatoire: DEFAULT_PRICER_CAPACITY_VALUE,
        capacity_method: CAPACITY_METHOD_PUISSANCE,
        abonnement: 0,
        turpe: false,
        prix_base: 0,
        prix_hp: 0,
        prix_hc: 0,
        prix_hph: 0,
        prix_hch: 0,
        prix_hpb: 0,
        prix_hcb: 0,
        prix_pte: 0,
        coefficient_capacite_base: DEFAULT_COEFF_CAPACITE_VALUE.WITH_COEFF,
        coefficient_capacite_hp: DEFAULT_COEFF_CAPACITE_VALUE.WITH_COEFF,
        coefficient_capacite_hc: DEFAULT_COEFF_CAPACITE_VALUE.WITH_COEFF,
        coefficient_capacite_hph: DEFAULT_COEFF_CAPACITE_VALUE.WITH_COEFF,
        coefficient_capacite_hch: DEFAULT_COEFF_CAPACITE_VALUE.WITH_COEFF,
        coefficient_capacite_hpb: DEFAULT_COEFF_CAPACITE_VALUE.WITH_COEFF,
        coefficient_capacite_hcb: DEFAULT_COEFF_CAPACITE_VALUE.WITH_COEFF,
        coefficient_capacite_pte: DEFAULT_COEFF_CAPACITE_VALUE.WITH_COEFF,
    }
}

/**
 * Ajoute une nouvelle période à la liste
 */
function onClickAddPeriod(): void {
    if (!periodList.value) {
        periodList.value = []
    }
    periodList.value.push(createDefaultPeriod())
}

/**
 * Duplique une période existante
 * @param index - Index de la période à dupliquer
 */
function onClickDuplicate(index: number): void {
    const period = periodList.value?.[index]
    if (period && periodList.value) {
        const duplicatedPeriod: PeriodItem = {
            ...period,
            idperiode: moment().add(1, "year").format("YYYY"),
            name: `${period.name} (copie)`,
        }
        periodList.value.push(duplicatedPeriod)
    }
}

/**
 * Supprime une période de la liste
 * @param index - Index de la période à supprimer
 */
function onClickDelete(index: number): void {
    if (periodList.value && periodList.value.length > 1) {
        periodList.value.splice(index, 1)
    }
}

/**
 * Met à jour une période spécifique et gère la validation globale
 * @param index - Index de la période à mettre à jour
 * @param event - Données de mise à jour
 */
function onUpdatePeriod(index: number, event: PeriodUpdateEvent): void {
    if (!periodList.value) return

    // Met à jour l'état de validation globale
    hasError.value = !event.valid

    // Met à jour la période dans la liste
    periodList.value.splice(index, 1, event.period)
}

// ================================
// COMPONENT EXPOSURE
// ================================

function validatePeriods(): void {
    periodItemRef.value?.forEach((periodItem) => {
        periodItem.validate()
    })
    validate()
}

defineExpose({
    hasError,
    validate: validatePeriods,
    expand: () => (active.value = "0"),
    collapse: () => (active.value = null),
})
</script>

<template>
    <!-- ================================ -->
    <!-- COMPOSANT PRINCIPAL - ACCORDÉON -->
    <!-- ================================ -->
    <BaseAccordion
        :value="active"
        @tab-close="active = null"
        @tab-open="active = '0'"
        class="comparator-offer-item-period"
    >
        <BaseAccordionPanel value="0">
            <!-- ================================ -->
            <!-- HEADER - TITRE ET VALIDATION -->
            <!-- ================================ -->
            <BaseAccordionHeader
                class="comparator-offer-item-period__header flex-row-reverse! justify-end! gap-2!"
            >
                <div class="flex gap-2 items-center justify-between w-full">
                    <div class="flex gap-2 items-center">
                        <h5 class="pl-2! text-xs sm:text-base">
                            {{ t("title") }}
                        </h5>
                        <!-- Indicateur de validation -->
                        <i
                            class="pi pi-check"
                            :class="{
                                'text-green-500': !hasError,
                                'text-red-500': hasError,
                            }"
                        ></i>
                    </div>
                    <BaseButton
                        link
                        :label="t('edit', { edit: active === '0' })"
                        :icon="`pi pi-${active === '0' ? 'check' : 'pencil'}`"
                        icon-pos="right"
                        :pt="{
                            label: 'text-primary-700',
                            icon: 'text-primary-700',
                        }"
                    ></BaseButton>
                </div>
            </BaseAccordionHeader>

            <!-- ================================ -->
            <!-- CONTENU PRINCIPAL -->
            <!-- ================================ -->
            <BaseAccordionContent :pt="{ content: 'rounded-b-md' }">
                <!-- ================================ -->
                <!-- LISTE DES PÉRIODES -->
                <!-- ================================ -->
                <TransitionGroup
                    name="list"
                    tag="ul"
                    class="flex flex-col gap-4"
                >
                    <li
                        v-for="(period, index) in periodList"
                        :key="`period-${period.idperiode}-${index}`"
                    >
                        <ComparatorOfferItemPeriodItem
                            ref="periodItemRef"
                            :actual="actual"
                            :index="actual ? null : index + 1"
                            :period="period"
                            :offer="offer"
                            :energieparposte-elec="energieparposteElec"
                            @update:period="onUpdatePeriod(index, $event)"
                            @click:duplicate="onClickDuplicate(index)"
                            @click:delete="onClickDelete(index)"
                        />
                    </li>
                </TransitionGroup>

                <!-- ================================ -->
                <!-- ACTIONS - AJOUT DE PÉRIODE -->
                <!-- ================================ -->
                <BaseButton
                    v-if="!actual"
                    :label="t('add_period')"
                    @click="onClickAddPeriod"
                    variant="link"
                    severity="secondary"
                    class="mt-4"
                    icon="pi pi-plus"
                />
            </BaseAccordionContent>
        </BaseAccordionPanel>
    </BaseAccordion>
</template>

<style scoped>
/* ================================ */
/* COMPOSANT PRINCIPAL */
/* ================================ */
.comparator-offer-item-period {
    border-radius: 0.375rem;
}

.comparator-offer-item-period__header {
    padding: 1rem;
}

/* ================================ */
/* ANIMATIONS DE TRANSITION */
/* ================================ */
.list-enter-active,
.list-leave-active {
    transition: all 0.5s ease;
}

.list-enter-from,
.list-leave-to {
    opacity: 0;
    transform: translateX(30px);
}

/* ================================ */
/* RESPONSIVE/BREAKPOINTS */
/* ================================ */
@media (max-width: 768px) {
    .comparator-offer-item-period {
        border-radius: 0.25rem;
    }
}
</style>
